import React, { useState, useEffect, useRef } from 'react';
import { 
  Crown, Users, Send, MessageSquare, Calendar, Target, 
  ChevronRight, Loader2, RefreshCw, Plus, Check, X,
  Video, Clock, FileText, TrendingUp, AlertTriangle,
  Sparkles, Bot, User, Building2, ArrowLeft, Mic, MicOff, Volume2, VolumeX,
  Brain, Trash2, BookOpen, Palette, Image
} from 'lucide-react';
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


function ComiteDirectionDashboard({ onBack, authUser }) {
  const [messages, setMessages] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showParticipantSelector, setShowParticipantSelector] = useState(false);
  const [reunions, setReunions] = useState([]);
  const [activeTab, setActiveTab] = useState('discussion'); // discussion, debat, creations, history
  const [showNewReunion, setShowNewReunion] = useState(false);
  const [reunionForm, setReunionForm] = useState({
    title: '',
    meeting_type: 'hebdo',
    agenda: ['']
  });
  
  // États pour les demandes de création (David)
  const [creationRequests, setCreationRequests] = useState([]);
  const [showNewCreationRequest, setShowNewCreationRequest] = useState(false);
  const [creationForm, setCreationForm] = useState({
    requester_id: '',
    creation_type: 'presentation',
    title: '',
    description: '',
    target_audience: '',
    urgency: 'normal',
    additional_instructions: ''
  });
  const [submittingCreation, setSubmittingCreation] = useState(false);
  
  // États pour l'audio
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioMode, setAudioMode] = useState('text'); // 'text', 'voice', 'conversation'
  const [playingAudio, setPlayingAudio] = useState(null); // ID du message en lecture
  const [audioEnabled, setAudioEnabled] = useState(true); // Activer/désactiver les réponses vocales
  const [isConversationActive, setIsConversationActive] = useState(false); // Mode conversation vocale
  const [liveTranscript, setLiveTranscript] = useState(''); // Transcription en temps réel
  const [isListening, setIsListening] = useState(false); // En écoute active
  const [isSpeaking, setIsSpeaking] = useState(false); // L'IA parle actuellement
  
  // États pour la base de connaissances
  const [showKnowledgeModal, setShowKnowledgeModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentKnowledge, setAgentKnowledge] = useState([]);
  const [loadingKnowledge, setLoadingKnowledge] = useState(false);
  const [newKnowledge, setNewKnowledge] = useState({ type: 'information', contenu: '' });
  
  // État pour la réunion sélectionnée (vue détaillée)
  const [selectedReunion, setSelectedReunion] = useState(null);
  const [reunionMessages, setReunionMessages] = useState([]);
  const [loadingReunion, setLoadingReunion] = useState(false);
  
  // États pour le mode débat
  const [debatSujet, setDebatSujet] = useState('');
  const [debatParticipants, setDebatParticipants] = useState([]);
  const [debatResponses, setDebatResponses] = useState([]);
  const [debatLoading, setDebatLoading] = useState(false);
  const [includeExternalConsultant, setIncludeExternalConsultant] = useState(false);
  const [externalModel, setExternalModel] = useState('gpt'); // gpt, claude, gemini
  const [debatHistory, setDebatHistory] = useState([]);
  const [debatSynthesis, setDebatSynthesis] = useState(null);
  const [debatConsensus, setDebatConsensus] = useState(false);
  const [debatTotalRounds, setDebatTotalRounds] = useState(1);
  const [continuingDebat, setContinuingDebat] = useState(false);
  const [pastDebats, setPastDebats] = useState([]); // Liste des débats passés pour l'historique
  
  // États pour l'intervention PDG
  const [showPdgPrompt, setShowPdgPrompt] = useState(false);
  const [pdgPrompt, setPdgPrompt] = useState('');
  const [pdgPromptSending, setPdgPromptSending] = useState(false);
  
  // États pour les mentions @ dans le prompt PDG
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionCursorPos, setMentionCursorPos] = useState(0);
  const pdgPromptRef = useRef(null);
  
  // États pour les participants humains
  const [humanCollaborators, setHumanCollaborators] = useState([]); // Liste des collaborateurs invitables
  const [selectedHumans, setSelectedHumans] = useState([]); // Humains sélectionnés pour le débat
  const [humanNotifyOptions, setHumanNotifyOptions] = useState({}); // {email: {notify_email, notify_app}}
  const [activeDebatId, setActiveDebatId] = useState(null); // Débat en cours pour interventions
  const [humanIntervention, setHumanIntervention] = useState(''); // Texte de l'intervention
  const [currentUserEmail, setCurrentUserEmail] = useState(''); // Email de l'utilisateur connecté
  
  // États pour le générateur de documents personnalisés
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [availableThemes, setAvailableThemes] = useState([]);
  const [docGeneratorForm, setDocGeneratorForm] = useState({
    template_type: 'formation',
    theme: 'default',
    title: '',
    subtitle: '',
    // Formation
    key_points: [],
    audience: 'Équipe',
    // Commercial
    client_name: '',
    services: [],
    total_price: '',
    // Rapport
    period: '',
    department: 'Direction',
    stats: [],
    // Présentation
    slides: []
  });
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [generatedDocResult, setGeneratedDocResult] = useState(null);
  const [davidTools, setDavidTools] = useState([]);
  const [generatingVisual, setGeneratingVisual] = useState(null);
  const [selectedTool, setSelectedTool] = useState('gpt_image');
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const recognitionRef = useRef(null); // Web Speech API
  const silenceTimeoutRef = useRef(null); // Timeout pour détecter le silence
  const lastTranscriptRef = useRef(''); // Dernier transcript complet
  const isConversationActiveRef = useRef(false); // Ref pour tracker l'état de conversation dans les closures

  useEffect(() => {
    loadData();
    // Cleanup Web Speech on unmount
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les directeurs IA
      const directorsResp = await authFetch(`${API}/direction/agents`);
      if (directorsResp.ok) {
        const data = await directorsResp.json();
        setDirectors(data.agents || []);
      }

      // Charger les conversations
      const messagesResp = await authFetch(`${API}/comite-direction/conversations?limit=50`);
      if (messagesResp.ok) {
        const data = await messagesResp.json();
        setMessages(data.messages || []);
      }

      // Charger les réunions
      const reunionsResp = await authFetch(`${API}/comite-direction/reunions?limit=10`);
      if (reunionsResp.ok) {
        const data = await reunionsResp.json();
        setReunions(data.reunions || []);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
    setLoading(false);
  };

  // Ouvrir une réunion pour voir son contenu
  const openReunion = async (reunion) => {
    console.log("Opening reunion:", reunion);
    setLoadingReunion(true);
    setSelectedReunion(reunion); // Set initial data
    
    try {
      // Charger les messages de cette réunion
      const resp = await authFetch(`${API}/comite-direction/reunion/${reunion.id}/messages`);
      if (resp.ok) {
        const data = await resp.json();
        console.log("Reunion data loaded:", data);
        setReunionMessages(data.messages || []);
        // Mettre à jour avec les données complètes de la réunion
        if (data.reunion) {
          setSelectedReunion(prev => ({
            ...prev,
            ...data.reunion,
            agenda: data.reunion.agenda || [],
            decisions: data.reunion.decisions || []
          }));
        }
      }
    } catch (error) {
      console.error('Erreur chargement réunion:', error);
    }
    setLoadingReunion(false);
  };

  const closeReunion = () => {
    setSelectedReunion(null);
    setReunionMessages([]);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const resp = await authFetch(`${API}/comite-direction/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: newMessage,
          participants: selectedParticipants,
          meeting_type: 'discussion'
        })
      });

      if (resp.ok) {
        setNewMessage('');
        setSelectedParticipants([]);
        // Recharger les messages
        await loadData();
      }
    } catch (error) {
      console.error('Erreur envoi message:', error);
    }
    setSending(false);
  };

  // ═══════════════════════════════════════════════════════════════
  // FONCTIONS CRÉATIONS - Demandes à David
  // ═══════════════════════════════════════════════════════════════

  const loadCreationRequests = async () => {
    try {
      const resp = await authFetch(`${API}/david/creation-requests`);
      if (resp.ok) {
        const data = await resp.json();
        setCreationRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Erreur chargement demandes:', error);
    }
  };

  // Charger les débats passés
  const loadPastDebats = async () => {
    try {
      const resp = await authFetch(`${API}/comite-direction/debats?limit=20`);
      if (resp.ok) {
        const data = await resp.json();
        setPastDebats(data.debats || []);
      }
    } catch (error) {
      console.error('Erreur chargement débats:', error);
    }
  };

  // Rouvrir un débat existant
  const reopenDebat = async (debat) => {
    try {
      // Charger les messages du débat
      const resp = await authFetch(`${API}/comite-direction/debat/${debat.debat_id}/messages`);
      if (resp.ok) {
        const data = await resp.json();
        const meta = data.meta || debat;
        
        // Mettre à jour les états du débat
        setActiveDebatId(debat.debat_id);
        setDebatSujet(meta.sujet || debat.sujet);
        setDebatParticipants(meta.participants || debat.participants || []);
        setDebatResponses(data.messages || []);
        setDebatTotalRounds(meta.total_rounds || debat.total_rounds || 1);
        setIncludeExternalConsultant(meta.include_external || debat.include_external || false);
        setExternalModel(meta.external_model || debat.external_model || 'gpt');
        setDebatSynthesis(null);
        setDebatConsensus(false);
        
        // Aller sur l'onglet débat
        setActiveTab('debat');
        
        console.log('Débat rouvert:', {
          id: debat.debat_id,
          participants: meta.participants || debat.participants,
          external: meta.include_external || debat.include_external
        });
      }
    } catch (error) {
      console.error('Erreur réouverture débat:', error);
      alert('Erreur lors de la réouverture du débat');
    }
  };

  // Charger les débats quand on va sur l'historique
  useEffect(() => {
    if (activeTab === 'history') {
      loadPastDebats();
    }
  }, [activeTab]);

  const submitCreationRequest = async () => {
    if (!creationForm.requester_id || !creationForm.title || !creationForm.description) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSubmittingCreation(true);
    try {
      const resp = await authFetch(`${API}/david/creation-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(creationForm)
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setCreationRequests(prev => [data.request, ...prev]);
        setShowNewCreationRequest(false);
        setCreationForm({
          requester_id: '',
          creation_type: 'presentation',
          title: '',
          description: '',
          target_audience: '',
          urgency: 'normal',
          additional_instructions: ''
        });
        alert(`✅ David a reçu la demande!\n\n${data.david_response}`);
      }
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert('Erreur lors de la soumission');
    }
    setSubmittingCreation(false);
  };

  const generateVisual = async (requestId, tool = 'gpt_image') => {
    setGeneratingVisual(requestId);
    try {
      const resp = await authFetch(`${API}/david/generate-visual?request_id=${requestId}&tool=${tool}`, {
        method: 'POST'
      });
      
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          loadCreationRequests();
          if (data.status === 'generating') {
            // Sora 2 background - poll every 30s
            const pollInterval = setInterval(async () => {
              const r = await authFetch(`${API}/david/creation-requests`);
              if (r.ok) {
                const d = await r.json();
                const req = (d.requests || []).find(x => x.request_id === requestId);
                if (req && (req.status === 'completed' || req.status === 'failed')) {
                  clearInterval(pollInterval);
                  setGeneratingVisual(null);
                  loadCreationRequests();
                }
              }
            }, 30000);
          } else {
            setGeneratingVisual(null);
          }
        } else {
          alert(data.message || 'Erreur lors de la generation');
          setGeneratingVisual(null);
        }
      } else {
        setGeneratingVisual(null);
      }
    } catch (error) {
      console.error('Erreur generation:', error);
      setGeneratingVisual(null);
    }
  };

  const loadDavidTools = async () => {
    try {
      const resp = await authFetch(`${API}/david/available-tools`);
      if (resp.ok) {
        const data = await resp.json();
        setDavidTools(data.tools || []);
        if (data.tools?.length > 0) setSelectedTool(data.tools[0].id);
      }
    } catch (e) { console.error('Erreur chargement outils David:', e); }
  };

  // Charger les créations quand on change d'onglet
  useEffect(() => {
    if (activeTab === 'creations') {
      loadCreationRequests();
      loadDocumentTemplates();
      loadDavidTools();
    }
  }, [activeTab]);

  // ═══════════════════════════════════════════════════════════════
  // FONCTIONS GÉNÉRATEUR DE DOCUMENTS PERSONNALISÉS
  // ═══════════════════════════════════════════════════════════════

  const loadDocumentTemplates = async () => {
    try {
      const resp = await authFetch(`${API}/documents/templates`);
      if (resp.ok) {
        const data = await resp.json();
        setAvailableTemplates(data.templates || []);
        setAvailableThemes(data.themes || []);
      }
    } catch (error) {
      console.error('Erreur chargement templates:', error);
    }
  };

  const generateCustomDocument = async () => {
    if (!docGeneratorForm.title.trim()) {
      alert('Veuillez saisir un titre pour le document');
      return;
    }
    
    setGeneratingDoc(true);
    try {
      const resp = await authFetch(`${API}/documents/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(docGeneratorForm)
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setGeneratedDocResult(data);
      } else {
        alert('Erreur lors de la génération du document');
      }
    } catch (error) {
      console.error('Erreur génération document:', error);
      alert('Erreur lors de la génération');
    }
    setGeneratingDoc(false);
  };

  const addKeyPoint = () => {
    setDocGeneratorForm(prev => ({
      ...prev,
      key_points: [...(prev.key_points || []), { title: '', description: '' }]
    }));
  };

  const updateKeyPoint = (index, field, value) => {
    setDocGeneratorForm(prev => ({
      ...prev,
      key_points: prev.key_points.map((kp, i) => 
        i === index ? { ...kp, [field]: value } : kp
      )
    }));
  };

  const removeKeyPoint = (index) => {
    setDocGeneratorForm(prev => ({
      ...prev,
      key_points: prev.key_points.filter((_, i) => i !== index)
    }));
  };

  const addService = () => {
    setDocGeneratorForm(prev => ({
      ...prev,
      services: [...(prev.services || []), { name: '', description: '', price: '' }]
    }));
  };

  const updateService = (index, field, value) => {
    setDocGeneratorForm(prev => ({
      ...prev,
      services: prev.services.map((s, i) => 
        i === index ? { ...s, [field]: value } : s
      )
    }));
  };

  const removeService = (index) => {
    setDocGeneratorForm(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const addStat = () => {
    setDocGeneratorForm(prev => ({
      ...prev,
      stats: [...(prev.stats || []), { value: '', label: '', trend: '' }]
    }));
  };

  const updateStat = (index, field, value) => {
    setDocGeneratorForm(prev => ({
      ...prev,
      stats: prev.stats.map((s, i) => 
        i === index ? { ...s, [field]: value } : s
      )
    }));
  };

  const removeStat = (index) => {
    setDocGeneratorForm(prev => ({
      ...prev,
      stats: prev.stats.filter((_, i) => i !== index)
    }));
  };

  const resetDocGeneratorForm = () => {
    setDocGeneratorForm({
      template_type: 'formation',
      theme: 'default',
      title: '',
      subtitle: '',
      key_points: [],
      audience: 'Équipe',
      client_name: '',
      services: [],
      total_price: '',
      period: '',
      department: 'Direction',
      stats: [],
      slides: []
    });
    setGeneratedDocResult(null);
  };

  // ═══════════════════════════════════════════════════════════════
  // FONCTIONS AUDIO - Enregistrement et lecture
  // ═══════════════════════════════════════════════════════════════

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Erreur accès microphone:', error);
      alert('Impossible d\'accéder au microphone. Vérifiez les permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const resp = await authFetch(`${API}/audio/transcribe?language=fr`, {
        method: 'POST',
        body: formData
      });

      if (resp.ok) {
        const data = await resp.json();
        if (data.text) {
          setNewMessage(data.text);
        }
      } else {
        console.error('Erreur transcription');
      }
    } catch (error) {
      console.error('Erreur transcription:', error);
    }
    setIsTranscribing(false);
  };

  const playMessageAudio = async (message) => {
    if (playingAudio === message.id) {
      // Arrêter la lecture
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudio(null);
      return;
    }

    try {
      setPlayingAudio(message.id);
      
      // Déterminer la voix selon le directeur
      const director = directors.find(d => d.director_id === message.sender_id);
      const voice = director?.voice || 'nova';

      // POST avec query params
      const resp = await authFetch(`${API}/audio/synthesize?text=${encodeURIComponent(message.content)}&voice=${voice}`, {
        method: 'POST'
      });
      
      if (resp.ok) {
        const data = await resp.json();
        if (data.audio_base64) {
          // Créer et jouer l'audio
          const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          audioRef.current = audio;
          
          audio.onended = () => {
            setPlayingAudio(null);
            audioRef.current = null;
          };
          
          await audio.play();
        }
      } else {
        console.error('Erreur TTS:', await resp.text());
        setPlayingAudio(null);
      }
    } catch (error) {
      console.error('Erreur lecture audio:', error);
      setPlayingAudio(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // MODE CONVERSATION VOCALE FLUIDE
  // Reconnaissance vocale continue + envoi automatique après pause
  // ═══════════════════════════════════════════════════════════════
  
  const startConversationMode = () => {
    // Vérifier si Web Speech API est supportée
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Votre navigateur ne supporte pas la reconnaissance vocale. Utilisez Chrome ou Edge.');
      return;
    }

    // Arrêter la lecture audio en cours
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'fr-FR';
    
    recognition.onstart = () => {
      isConversationActiveRef.current = true;
      setIsConversationActive(true);
      setIsListening(true);
      setLiveTranscript('');
      lastTranscriptRef.current = '';
      console.log('[CONVERSATION] Écoute démarrée');
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Mise à jour de la transcription en temps réel
      const currentTranscript = lastTranscriptRef.current + finalTranscript;
      lastTranscriptRef.current = currentTranscript;
      setLiveTranscript(currentTranscript + interimTranscript);

      // Reset le timer de silence à chaque nouveau résultat
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }

      // Si on a du texte final, démarrer le timer de silence (2 secondes)
      if (currentTranscript.trim()) {
        console.log('[CONVERSATION] Texte détecté, timer 2s lancé:', currentTranscript);
        silenceTimeoutRef.current = setTimeout(() => {
          // Pause détectée - envoyer le message
          const textToSend = lastTranscriptRef.current.trim();
          console.log('[CONVERSATION] Timeout expiré, isActive:', isConversationActiveRef.current, 'text:', textToSend);
          if (textToSend && isConversationActiveRef.current) {
            console.log('[CONVERSATION] Envoi du message:', textToSend);
            // Stopper la reconnaissance avant d'envoyer
            recognition.stop();
            sendConversationMessage(textToSend, recognition);
            lastTranscriptRef.current = '';
            setLiveTranscript('');
          }
        }, 2000); // 2 secondes de silence
      }
    };

    recognition.onerror = (event) => {
      console.error('[CONVERSATION] Erreur:', event.error);
      if (event.error === 'no-speech') {
        // Pas de parole détectée - relancer si toujours en mode conversation
        if (isConversationActiveRef.current) {
          try {
            recognition.start();
          } catch (e) {
            console.log('[CONVERSATION] Déjà en cours');
          }
        }
      } else if (event.error !== 'aborted') {
        isConversationActiveRef.current = false;
        setIsConversationActive(false);
        setIsListening(false);
      }
    };

    recognition.onend = () => {
      console.log('[CONVERSATION] Reconnaissance terminée, isActive:', isConversationActiveRef.current);
      // Ne pas relancer automatiquement ici - c'est géré par sendConversationMessage
      if (!isConversationActiveRef.current) {
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopConversationMode = () => {
    isConversationActiveRef.current = false;
    setIsConversationActive(false);
    setIsListening(false);
    setLiveTranscript('');
    lastTranscriptRef.current = '';
    
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
    
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    console.log('[CONVERSATION] Mode conversation arrêté');
  };

  const sendConversationMessage = async (text, recognition) => {
    if (!text.trim() || sending) return;
    
    setIsListening(false);
    setSending(true);
    
    try {
      const resp = await authFetch(`${API}/comite-direction/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: text,
          participants: selectedParticipants,
          meeting_type: 'discussion'
        })
      });

      if (resp.ok) {
        const data = await resp.json();
        // Recharger les messages
        await loadData();
        
        // Jouer la réponse vocale du premier agent
        if (data.responses && data.responses.length > 0 && audioEnabled) {
          await playConversationResponse(data.responses[0], recognition);
        } else {
          // Pas de réponse vocale, relancer l'écoute
          restartListening(recognition);
        }
      } else {
        restartListening(recognition);
      }
    } catch (error) {
      console.error('[CONVERSATION] Erreur envoi:', error);
      restartListening(recognition);
    }
    
    setSending(false);
  };

  const restartListening = (recognition) => {
    if (isConversationActiveRef.current) {
      // Utiliser la recognition passée ou celle stockée dans la ref
      const rec = recognition || recognitionRef.current;
      if (rec) {
        try {
          setIsListening(true);
          rec.start();
          console.log('[CONVERSATION] Écoute relancée');
        } catch (e) {
          // Si erreur "already started", c'est ok
          if (e.message?.includes('already started')) {
            console.log('[CONVERSATION] Écoute déjà active');
            setIsListening(true);
          } else {
            console.log('[CONVERSATION] Erreur relance, recréation:', e);
            // Recréer la reconnaissance vocale
            setTimeout(() => {
              if (isConversationActiveRef.current) {
                startConversationMode();
              }
            }, 500);
          }
        }
      } else {
        console.log('[CONVERSATION] Pas de recognition, redémarrage');
        setTimeout(() => {
          if (isConversationActiveRef.current) {
            startConversationMode();
          }
        }, 500);
      }
    }
  };

  const playConversationResponse = async (response, recognition) => {
    setIsSpeaking(true);
    setIsListening(false);
    
    try {
      const director = directors.find(d => d.director_id === response.sender_id);
      const voice = director?.voice || 'nova';
      
      console.log('[CONVERSATION] Lecture réponse de', response.sender_name, 'voix:', voice);
      
      // Créer un AbortController pour le timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 secondes max
      
      // POST avec query params pour TTS
      const audioResp = await authFetch(`${API}/audio/synthesize?text=${encodeURIComponent(response.content)}&voice=${voice}`, {
        method: 'POST',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (audioResp.ok) {
        const data = await audioResp.json();
        if (data.audio_base64) {
          const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          audioRef.current = audio;
          
          audio.onended = () => {
            console.log('[CONVERSATION] Fin lecture audio');
            setIsSpeaking(false);
            audioRef.current = null;
            
            // Relancer l'écoute après la fin de la réponse
            restartListening(recognition);
          };
          
          audio.onerror = () => {
            console.log('[CONVERSATION] Erreur audio');
            setIsSpeaking(false);
            audioRef.current = null;
            restartListening(recognition);
          };
          
          await audio.play();
        } else {
          console.log('[CONVERSATION] Pas d\'audio dans la réponse');
          setIsSpeaking(false);
          restartListening(recognition);
        }
      } else {
        console.error('[CONVERSATION] Erreur TTS:', await audioResp.text());
        setIsSpeaking(false);
        restartListening(recognition);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[CONVERSATION] TTS timeout - on continue sans voix');
      } else {
        console.error('[CONVERSATION] Erreur lecture réponse:', error);
      }
      setIsSpeaking(false);
      restartListening(recognition);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // MODE DÉBAT MULTI-AGENTS
  // ═══════════════════════════════════════════════════════════════

  // Charger les collaborateurs humains invitables
  const loadHumanCollaborators = async () => {
    try {
      const resp = await authFetch(`${API}/comite-direction/collaborateurs-invitables`);
      if (resp.ok) {
        const data = await resp.json();
        setHumanCollaborators(data.collaborateurs || []);
      }
    } catch (error) {
      console.error('Erreur chargement collaborateurs:', error);
    }
  };

  // Récupérer l'email de l'utilisateur connecté
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUserEmail(user.email || '');
      } catch (e) {}
    }
    loadHumanCollaborators();
  }, []);

  const startDebat = async () => {
    if (!debatSujet.trim() || (debatParticipants.length < 2 && selectedHumans.length === 0)) {
      alert('Veuillez entrer un sujet et sélectionner au moins 2 participants (IA ou humains)');
      return;
    }
    
    setDebatLoading(true);
    setDebatResponses([]);
    
    try {
      // Préparer les participants humains avec leurs options de notification
      const humanParticipantsPayload = selectedHumans.map(email => ({
        email,
        notify_email: humanNotifyOptions[email]?.notify_email || false,
        notify_app: humanNotifyOptions[email]?.notify_app !== false // true par défaut
      }));

      const resp = await authFetch(`${API}/comite-direction/debat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sujet: debatSujet,
          participants: debatParticipants,
          human_participants: humanParticipantsPayload,
          include_external: includeExternalConsultant,
          external_model: externalModel,
          max_rounds: 3,
          auto_continue: true
        })
      });

      if (resp.ok) {
        const data = await resp.json();
        setDebatResponses(data.responses || []);
        setActiveDebatId(data.debat_id);
        setDebatSynthesis(data.synthesis || null);
        setDebatConsensus(data.consensus_reached || false);
        setDebatTotalRounds(data.total_rounds || 1);
        // Recharger l'historique des débats
        loadDebatHistory();
      } else {
        console.error('Erreur débat:', await resp.text());
        alert('Erreur lors du lancement du débat');
      }
    } catch (error) {
      console.error('Erreur débat:', error);
      alert('Erreur de connexion');
    }
    setDebatLoading(false);
  };

  // Continuer le débat avec un nouveau tour
  const continueDebat = async () => {
    if (!activeDebatId) return;
    
    setContinuingDebat(true);
    try {
      const resp = await authFetch(`${API}/comite-direction/debat/continuer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          debat_id: activeDebatId,
          additional_context: ""
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        // Ajouter les nouvelles réponses
        setDebatResponses(prev => [...prev, ...data.new_responses]);
        setDebatTotalRounds(data.current_round);
      } else {
        alert('Erreur lors de la continuation du débat');
      }
    } catch (error) {
      console.error('Erreur continuation:', error);
    }
    setContinuingDebat(false);
  };

  // Obtenir la liste des participants mentionnables
  const getMentionableParticipants = () => {
    const participants = [];
    
    // Ajouter les agents IA du débat
    debatParticipants.forEach(pid => {
      const director = directors.find(d => d.director_id === pid);
      if (director) {
        participants.push({
          id: director.director_id,
          name: director.name,
          avatar: director.avatar,
          type: 'agent',
          searchName: director.name.toLowerCase()
        });
      }
    });
    
    // Ajouter le consultant externe si présent
    if (includeExternalConsultant) {
      const extNames = {
        gpt: { name: 'Dr. GPT', avatar: '🤖' },
        claude: { name: 'Dr. Claude', avatar: '🧠' },
        gemini: { name: 'Dr. Gemini', avatar: '💎' }
      };
      const ext = extNames[externalModel] || { name: 'Consultant', avatar: '🎓' };
      participants.push({
        id: `external_${externalModel}`,
        name: ext.name,
        avatar: ext.avatar,
        type: 'external',
        searchName: ext.name.toLowerCase()
      });
    }
    
    return participants;
  };

  // Gérer la saisie dans le prompt PDG pour détecter les @
  const handlePdgPromptChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setPdgPrompt(value);
    
    // Chercher si on est en train de taper une mention
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Vérifier qu'il n'y a pas d'espace après le @
      if (!textAfterAt.includes(' ')) {
        setMentionSearch(textAfterAt.toLowerCase());
        setMentionCursorPos(lastAtIndex);
        setShowMentionList(true);
        return;
      }
    }
    
    setShowMentionList(false);
    setMentionSearch('');
  };

  // Insérer une mention dans le prompt
  const insertMention = (participant) => {
    const beforeAt = pdgPrompt.substring(0, mentionCursorPos);
    const afterMention = pdgPrompt.substring(mentionCursorPos + mentionSearch.length + 1);
    const newPrompt = `${beforeAt}@${participant.name} ${afterMention}`;
    setPdgPrompt(newPrompt);
    setShowMentionList(false);
    setMentionSearch('');
    
    // Remettre le focus sur le textarea
    setTimeout(() => {
      if (pdgPromptRef.current) {
        pdgPromptRef.current.focus();
        const newCursorPos = beforeAt.length + participant.name.length + 2;
        pdgPromptRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 10);
  };

  // Extraire les mentions du prompt
  const extractMentions = (text) => {
    const mentions = [];
    const participants = getMentionableParticipants();
    
    participants.forEach(p => {
      if (text.includes(`@${p.name}`)) {
        mentions.push(p.id);
      }
    });
    
    return mentions;
  };

  // Intervention PDG pour guider le débat
  const submitPdgIntervention = async () => {
    if (!pdgPrompt.trim() || !activeDebatId) return;
    
    setPdgPromptSending(true);
    try {
      // Extraire les mentions
      const targetedParticipants = extractMentions(pdgPrompt);
      
      // Ajouter le message du PDG
      const pdgMessage = {
        order: debatResponses.length + 1,
        round: debatTotalRounds,
        sender_id: "pdg",
        sender_name: "👑 PDG (Vous)",
        sender_avatar: "👑",
        sender_role: "PDG",
        content: pdgPrompt,
        is_pdg: true
      };
      
      setDebatResponses(prev => [...prev, pdgMessage]);
      
      // Continuer le débat avec le contexte du PDG et les mentions ciblées
      const resp = await authFetch(`${API}/comite-direction/debat/continuer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          debat_id: activeDebatId,
          additional_context: pdgPrompt,
          targeted_participants: targetedParticipants.length > 0 ? targetedParticipants : null
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setDebatResponses(prev => [...prev, ...data.new_responses]);
        setDebatTotalRounds(data.current_round);
      }
      
      setPdgPrompt('');
      setShowPdgPrompt(false);
      setShowMentionList(false);
    } catch (error) {
      console.error('Erreur intervention PDG:', error);
    }
    setPdgPromptSending(false);
  };

  // Permettre à un humain d'intervenir dans le débat
  const submitHumanIntervention = async () => {
    if (!humanIntervention.trim() || !activeDebatId || !currentUserEmail) {
      alert('Veuillez écrire votre intervention');
      return;
    }

    try {
      const resp = await authFetch(`${API}/comite-direction/debat/intervenir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          debat_id: activeDebatId,
          content: humanIntervention,
          user_email: currentUserEmail
        })
      });

      if (resp.ok) {
        const data = await resp.json();
        // Ajouter l'intervention à la liste des réponses
        setDebatResponses(prev => [...prev, data.message]);
        setHumanIntervention('');
      } else {
        alert('Erreur lors de l\'envoi de l\'intervention');
      }
    } catch (error) {
      console.error('Erreur intervention:', error);
      alert('Erreur de connexion');
    }
  };

  const loadDebatHistory = async () => {
    try {
      const resp = await authFetch(`${API}/comite-direction/debats?limit=10`);
      if (resp.ok) {
        const data = await resp.json();
        setDebatHistory(data.debats || []);
      }
    } catch (error) {
      console.error('Erreur chargement historique débats:', error);
    }
  };

  const toggleDebatParticipant = (directorId) => {
    setDebatParticipants(prev => 
      prev.includes(directorId)
        ? prev.filter(id => id !== directorId)
        : [...prev, directorId]
    );
  };

  const toggleHumanParticipant = (email) => {
    setSelectedHumans(prev => 
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
    // Initialiser les options de notification par défaut
    if (!humanNotifyOptions[email]) {
      setHumanNotifyOptions(prev => ({
        ...prev,
        [email]: { notify_email: false, notify_app: true }
      }));
    }
  };

  const selectAllDebatParticipants = () => {
    const allIds = directors.map(d => d.director_id);
    setDebatParticipants(allIds);
  };

  const clearDebatParticipants = () => {
    setDebatParticipants([]);
  };

  const clearHumanParticipants = () => {
    setSelectedHumans([]);
  };

  const resetDebat = () => {
    setDebatSujet('');
    setDebatParticipants([]);
    setDebatResponses([]);
    setIncludeExternalConsultant(false);
    setExternalModel('gpt');
    setSelectedHumans([]);
    setHumanNotifyOptions({});
    setActiveDebatId(null);
    setHumanIntervention('');
    setDebatSynthesis(null);
    setDebatConsensus(false);
    setDebatTotalRounds(1);
  };

  const startReunion = async () => {
    if (!reunionForm.title.trim()) return;
    
    try {
      const resp = await authFetch(`${API}/comite-direction/reunion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...reunionForm,
          agenda: reunionForm.agenda.filter(a => a.trim()),
          participants: selectedParticipants
        })
      });

      if (resp.ok) {
        setShowNewReunion(false);
        setReunionForm({ title: '', meeting_type: 'hebdo', agenda: [''] });
        await loadData();
      }
    } catch (error) {
      console.error('Erreur création réunion:', error);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // GESTION DE LA BASE DE CONNAISSANCES DES AGENTS
  // ═══════════════════════════════════════════════════════════════
  
  const openKnowledgeModal = async (agent) => {
    setSelectedAgent(agent);
    setShowKnowledgeModal(true);
    setLoadingKnowledge(true);
    
    try {
      const resp = await authFetch(`${API}/direction/agents/${agent.director_id}/knowledge`);
      if (resp.ok) {
        const data = await resp.json();
        setAgentKnowledge(data.knowledge_base || []);
      }
    } catch (error) {
      console.error('Erreur chargement connaissances:', error);
    }
    setLoadingKnowledge(false);
  };

  const addKnowledge = async () => {
    if (!newKnowledge.contenu.trim() || !selectedAgent) return;
    
    try {
      const resp = await authFetch(`${API}/direction/agents/${selectedAgent.director_id}/knowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newKnowledge)
      });
      
      if (resp.ok) {
        // Recharger les connaissances
        await openKnowledgeModal(selectedAgent);
        setNewKnowledge({ type: 'information', contenu: '' });
      }
    } catch (error) {
      console.error('Erreur ajout connaissance:', error);
    }
  };

  const deleteKnowledge = async (index) => {
    if (!selectedAgent) return;
    
    try {
      const resp = await authFetch(`${API}/direction/agents/${selectedAgent.director_id}/knowledge/${index}`, {
        method: 'DELETE'
      });
      
      if (resp.ok) {
        // Recharger les connaissances
        await openKnowledgeModal(selectedAgent);
      }
    } catch (error) {
      console.error('Erreur suppression connaissance:', error);
    }
  };

  const clearAllKnowledge = async () => {
    if (!selectedAgent || !window.confirm(`Êtes-vous sûr de vouloir vider toutes les connaissances de ${selectedAgent.name} ?`)) return;
    
    try {
      const resp = await authFetch(`${API}/direction/agents/${selectedAgent.director_id}/knowledge`, {
        method: 'DELETE'
      });
      
      if (resp.ok) {
        setAgentKnowledge([]);
      }
    } catch (error) {
      console.error('Erreur vidage connaissances:', error);
    }
  };

  const toggleParticipant = (directorId) => {
    setSelectedParticipants(prev => 
      prev.includes(directorId)
        ? prev.filter(id => id !== directorId)
        : [...prev, directorId]
    );
  };

  const getDirectorById = (id) => directors.find(d => d.director_id === id);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
    if (date.toDateString() === yesterday.toDateString()) return "Hier";
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  // Grouper les messages par date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = formatDate(msg.timestamp);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  // Hiérarchie des directeurs
  const dg = directors.find(d => d.director_id === 'dg');
  const dsi = directors.find(d => d.director_id === 'dsi');
  const otherDirectors = directors.filter(d => !['dg', 'dsi'].includes(d.director_id) && d.status === 'active');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-400 mx-auto mb-4" />
          <p className="text-slate-400">Chargement du Comité de Direction...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-600/20 via-orange-600/20 to-red-600/20 border-b border-amber-500/30 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Comité de Direction IA</h1>
              <p className="text-amber-300/80 text-sm">
                {directors.filter(d => d.status === 'active').length} directeurs actifs • PDG: Vous
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowNewReunion(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 rounded-lg transition-colors"
            >
              <Video className="w-4 h-4" />
              Nouvelle Réunion
            </button>
            <button
              onClick={loadData}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              title="Rafraîchir"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-100px)]">
        {/* Sidebar - Organigramme */}
        <aside className="w-80 border-r border-slate-700/50 bg-slate-800/30 p-4 overflow-y-auto">
          {/* PDG (Vous) */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3 text-xs text-slate-500 uppercase tracking-wider">
              <Crown className="w-4 h-4" />
              Présidence
            </div>
            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center text-2xl">
                  👑
                </div>
                <div>
                  <p className="font-bold text-white">PDG</p>
                  <p className="text-sm text-amber-300">Vous</p>
                </div>
              </div>
            </div>
          </div>

          {/* DG */}
          {dg && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 text-xs text-slate-500 uppercase tracking-wider">
                <Building2 className="w-4 h-4" />
                Direction Générale
              </div>
              <div 
                className={`border rounded-xl p-4 cursor-pointer transition-all ${
                  selectedParticipants.includes(dg.director_id)
                    ? 'bg-amber-500/20 border-amber-500/50'
                    : 'bg-slate-700/30 border-slate-600/30 hover:border-slate-500/50'
                }`}
                onClick={() => toggleParticipant(dg.director_id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center text-xl">
                    {dg.avatar}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{dg.name}</p>
                    <p className="text-xs text-slate-400">{dg.pole}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); openKnowledgeModal(dg); }}
                    className="p-1.5 hover:bg-purple-500/20 rounded-lg text-purple-400 transition-colors"
                    title="Base de connaissances"
                  >
                    <Brain className="w-4 h-4" />
                  </button>
                  {selectedParticipants.includes(dg.director_id) && (
                    <Check className="w-5 h-5 text-amber-400" />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* DSI */}
          {dsi && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 text-xs text-slate-500 uppercase tracking-wider">
                <Sparkles className="w-4 h-4" />
                Direction IT & IA
              </div>
              <div 
                className={`border rounded-xl p-3 cursor-pointer transition-all ${
                  selectedParticipants.includes(dsi.director_id)
                    ? 'bg-cyan-500/20 border-cyan-500/50'
                    : 'bg-slate-700/30 border-slate-600/30 hover:border-slate-500/50'
                }`}
                onClick={() => toggleParticipant(dsi.director_id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center text-xl">
                    {dsi.avatar}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{dsi.name}</p>
                    <p className="text-xs text-slate-400">{dsi.pole}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); openKnowledgeModal(dsi); }}
                    className="p-1.5 hover:bg-purple-500/20 rounded-lg text-purple-400 transition-colors"
                    title="Base de connaissances"
                  >
                    <Brain className="w-4 h-4" />
                  </button>
                  {selectedParticipants.includes(dsi.director_id) && (
                    <Check className="w-5 h-5 text-cyan-400" />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Directeurs de pôle */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-xs text-slate-500 uppercase tracking-wider">
              <Users className="w-4 h-4" />
              Directeurs de Pôle
            </div>
            <div className="space-y-2">
              {otherDirectors.map(director => (
                <div 
                  key={director.director_id}
                  className={`border rounded-lg p-3 cursor-pointer transition-all ${
                    selectedParticipants.includes(director.director_id)
                      ? 'bg-blue-500/20 border-blue-500/50'
                      : 'bg-slate-700/30 border-slate-600/30 hover:border-slate-500/50'
                  }`}
                  onClick={() => toggleParticipant(director.director_id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-600 rounded-lg flex items-center justify-center text-lg">
                      {director.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm truncate">{director.name}</p>
                      <p className="text-xs text-slate-400 truncate">{director.pole}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); openKnowledgeModal(director); }}
                      className="p-1 hover:bg-purple-500/20 rounded text-purple-400 transition-colors flex-shrink-0"
                      title="Base de connaissances"
                    >
                      <Brain className="w-3.5 h-3.5" />
                    </button>
                    {selectedParticipants.includes(director.director_id) && (
                      <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Indication participants */}
          {selectedParticipants.length > 0 && (
            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-300">
                {selectedParticipants.length} participant(s) sélectionné(s)
              </p>
              <button
                onClick={() => setSelectedParticipants([])}
                className="text-xs text-blue-400 hover:text-blue-300 mt-1"
              >
                Réinitialiser (envoyer à tous)
              </button>
            </div>
          )}
        </aside>

        {/* Zone principale - Conversation */}
        <main className="flex-1 flex flex-col">
          {/* Onglets */}
          <div className="flex gap-2 p-4 border-b border-slate-700/50">
            {[
              { id: 'discussion', label: 'Discussion', icon: MessageSquare },
              { id: 'debat', label: '🎭 Salle de Débat', icon: Users },
              { id: 'creations', label: '🎨 Créations', icon: Palette },
              { id: 'history', label: 'Historique', icon: Clock }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'discussion' && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mb-4">
                      <Crown className="w-10 h-10 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">Bienvenue au Comité de Direction</h3>
                    <p className="text-slate-400 max-w-md">
                      Échangez avec vos directeurs IA. Sélectionnez des participants dans la sidebar 
                      ou envoyez un message à tout le comité.
                    </p>
                  </div>
                ) : (
                  Object.entries(groupedMessages).map(([date, msgs]) => (
                    <div key={date}>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="h-px flex-1 bg-slate-700/50"></div>
                        <span className="text-xs text-slate-500 uppercase tracking-wider">{date}</span>
                        <div className="h-px flex-1 bg-slate-700/50"></div>
                      </div>
                      <div className="space-y-4">
                        {msgs.map((msg) => (
                          <div 
                            key={msg.id}
                            className={`flex gap-3 ${msg.sender_role === 'pdg' ? 'flex-row-reverse' : ''}`}
                          >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${
                              msg.sender_role === 'pdg' 
                                ? 'bg-amber-500' 
                                : msg.sender_role === 'dg'
                                  ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                                  : 'bg-slate-600'
                            }`}>
                              {msg.sender_avatar || '👤'}
                            </div>
                            <div className={`max-w-[70%] ${msg.sender_role === 'pdg' ? 'items-end' : ''}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`font-medium text-sm ${
                                  msg.sender_role === 'pdg' 
                                    ? 'text-amber-400' 
                                    : msg.sender_role === 'dg'
                                      ? 'text-orange-400'
                                      : 'text-slate-300'
                                }`}>
                                  {msg.sender_name}
                                </span>
                                <span className="text-xs text-slate-500">{formatTime(msg.timestamp)}</span>
                                {/* Bouton écouter (seulement pour les réponses des directeurs) */}
                                {msg.sender_role !== 'pdg' && audioEnabled && (
                                  <button
                                    onClick={() => playMessageAudio(msg)}
                                    className={`ml-2 p-1 rounded-lg transition-colors ${
                                      playingAudio === msg.id 
                                        ? 'bg-purple-500/30 text-purple-400' 
                                        : 'hover:bg-slate-700/50 text-slate-500 hover:text-purple-400'
                                    }`}
                                    title={playingAudio === msg.id ? 'Arrêter' : 'Écouter'}
                                  >
                                    {playingAudio === msg.id ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <Volume2 className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                )}
                              </div>
                              <div className={`rounded-xl px-4 py-3 ${
                                msg.sender_role === 'pdg'
                                  ? 'bg-amber-500/20 border border-amber-500/30'
                                  : 'bg-slate-700/50 border border-slate-600/30'
                              }`}>
                                <p className="text-white whitespace-pre-wrap">{msg.content}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Zone de saisie avec options vocales */}
              <div className="p-4 border-t border-slate-700/50 bg-slate-800/30">
                {/* Mode Conversation Active - Affichage spécial */}
                {isConversationActive ? (
                  <div className="space-y-4">
                    {/* Indicateur de conversation active */}
                    <div className={`p-4 rounded-xl border-2 transition-all ${
                      isSpeaking 
                        ? 'bg-purple-500/10 border-purple-500/50' 
                        : isListening 
                          ? 'bg-green-500/10 border-green-500/50 animate-pulse' 
                          : sending
                            ? 'bg-amber-500/10 border-amber-500/50'
                            : 'bg-slate-700/30 border-slate-600/50'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isSpeaking ? 'bg-purple-500' : isListening ? 'bg-green-500' : 'bg-amber-500'
                          }`}>
                            {isSpeaking ? (
                              <Volume2 className="w-5 h-5 text-white animate-pulse" />
                            ) : isListening ? (
                              <Mic className="w-5 h-5 text-white" />
                            ) : (
                              <Loader2 className="w-5 h-5 text-white animate-spin" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-white">
                              {isSpeaking ? 'L\'IA répond...' : isListening ? 'À votre écoute...' : 'Traitement...'}
                            </p>
                            <p className="text-xs text-slate-400">
                              {isSpeaking 
                                ? 'Attendez la fin de la réponse' 
                                : isListening 
                                  ? 'Parlez naturellement, pause de 2s pour envoyer'
                                  : 'Envoi du message en cours'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Bouton pour passer la réponse vocale */}
                          {isSpeaking && (
                            <button
                              onClick={() => {
                                // Arrêter l'audio en cours
                                if (audioRef.current) {
                                  audioRef.current.pause();
                                  audioRef.current = null;
                                }
                                setIsSpeaking(false);
                                
                                // Relancer la reconnaissance vocale
                                if (isConversationActiveRef.current && recognitionRef.current) {
                                  try {
                                    setIsListening(true);
                                    recognitionRef.current.start();
                                    console.log('[CONVERSATION] Écoute relancée après Passer');
                                  } catch (e) {
                                    console.log('[CONVERSATION] Erreur relance après Passer:', e);
                                    // Si déjà en cours, c'est ok
                                  }
                                } else if (isConversationActiveRef.current) {
                                  // Recréer la reconnaissance si nécessaire
                                  startConversationMode();
                                }
                              }}
                              className="px-3 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 rounded-lg flex items-center gap-2 transition-colors text-sm"
                            >
                              <ChevronRight className="w-4 h-4" />
                              Passer
                            </button>
                          )}
                          {/* Bouton pour relancer l'écoute si bloqué */}
                          {isListening && !isSpeaking && !sending && (
                            <button
                              onClick={() => {
                                console.log('[CONVERSATION] Relance manuelle de l\'écoute');
                                // Arrêter l'ancienne reconnaissance
                                if (recognitionRef.current) {
                                  try {
                                    recognitionRef.current.stop();
                                  } catch (e) {}
                                }
                                // Recréer une nouvelle session
                                setTimeout(() => {
                                  if (isConversationActiveRef.current) {
                                    startConversationMode();
                                  }
                                }, 300);
                              }}
                              className="px-3 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 rounded-lg flex items-center gap-2 transition-colors text-sm"
                              title="Cliquez si le micro ne répond plus"
                            >
                              <RefreshCw className="w-4 h-4" />
                              Relancer
                            </button>
                          )}
                          <button
                            onClick={stopConversationMode}
                            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg flex items-center gap-2 transition-colors"
                          >
                            <X className="w-4 h-4" />
                            Arrêter
                          </button>
                        </div>
                      </div>
                      
                      {/* Transcription en temps réel */}
                      {liveTranscript && (
                        <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                          <p className="text-xs text-slate-500 mb-1">Vous dites :</p>
                          <p className="text-white">{liveTranscript}</p>
                        </div>
                      )}
                      
                      {/* Participants sélectionnés */}
                      {selectedParticipants.length > 0 && (
                        <div className="flex items-center gap-2 mt-3">
                          <span className="text-xs text-slate-500">Conversation avec :</span>
                          {selectedParticipants.map(id => {
                            const dir = getDirectorById(id);
                            return dir ? (
                              <span key={id} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs">
                                {dir.avatar} {dir.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Toggle Mode Voix/Texte et options */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setAudioMode(audioMode === 'text' ? 'voice' : 'text')}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            audioMode === 'voice' 
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                              : 'bg-slate-700/50 text-slate-400 border border-slate-600/30'
                          }`}
                        >
                          {audioMode === 'voice' ? <Mic className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                          {audioMode === 'voice' ? 'Mode vocal' : 'Mode texte'}
                        </button>
                        
                        {/* Bouton Mode Conversation Fluide */}
                        <button
                          onClick={startConversationMode}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 border border-green-500/30 hover:from-green-500/30 hover:to-emerald-500/30 transition-colors"
                          title="Conversation vocale fluide - Parlez naturellement, pause automatique après 2s"
                        >
                          <div className="relative">
                            <Mic className="w-4 h-4" />
                            <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-ping"></div>
                          </div>
                          Conversation
                        </button>
                      </div>
                      <button
                        onClick={() => setAudioEnabled(!audioEnabled)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                          audioEnabled 
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-slate-700/50 text-slate-500'
                        }`}
                        title={audioEnabled ? 'Réponses vocales activées' : 'Réponses vocales désactivées'}
                      >
                        {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                        {audioEnabled ? 'Voix ON' : 'Voix OFF'}
                      </button>
                    </div>

                    {selectedParticipants.length > 0 && (
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <span className="text-xs text-slate-500">À:</span>
                        {selectedParticipants.map(id => {
                          const dir = getDirectorById(id);
                          return dir ? (
                            <span key={id} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs flex items-center gap-1">
                              {dir.avatar} {dir.name}
                              <button onClick={() => toggleParticipant(id)} className="hover:text-white">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                    
                    <div className="flex gap-3">
                      {/* Bouton Micro (mode vocal) */}
                      {audioMode === 'voice' && (
                        <button
                          onClick={isRecording ? stopRecording : startRecording}
                          disabled={isTranscribing}
                          className={`p-3 rounded-xl transition-all ${
                            isRecording 
                              ? 'bg-red-500 hover:bg-red-400 animate-pulse'
                              : isTranscribing
                                ? 'bg-purple-500/50 cursor-wait'
                                : 'bg-purple-500 hover:bg-purple-400'
                          }`}
                          title={isRecording ? 'Cliquez pour arrêter' : 'Cliquez pour parler'}
                        >
                          {isTranscribing ? (
                            <Loader2 className="w-5 h-5 animate-spin text-white" />
                          ) : isRecording ? (
                            <MicOff className="w-5 h-5 text-white" />
                          ) : (
                            <Mic className="w-5 h-5 text-white" />
                          )}
                        </button>
                      )}
                      
                      {/* Zone de texte */}
                      <input
                        ref={inputRef}
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                        placeholder={
                          isTranscribing 
                            ? "Transcription en cours..."
                            : isRecording
                              ? "🎙️ Parlez maintenant..."
                              : selectedParticipants.length > 0 
                                ? "Écrire aux participants sélectionnés..." 
                                : "Écrire à tout le comité..."
                        }
                        className={`flex-1 bg-slate-900 border rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none transition-colors ${
                          isRecording 
                            ? 'border-red-500/50 bg-red-500/5' 
                            : 'border-slate-700 focus:border-amber-500/50'
                        }`}
                      />
                      
                      {/* Bouton Envoyer */}
                      <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sending}
                        className="px-6 py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-xl text-white font-medium flex items-center gap-2 transition-colors"
                      >
                        {sending ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    
                    {/* Indication mode vocal */}
                    {audioMode === 'voice' && (
                      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                        <Mic className="w-3 h-3" />
                        Cliquez sur le micro pour dicter votre message (OpenAI Whisper)
                      </p>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* ONGLET DÉBAT MULTI-AGENTS */}
          {activeTab === 'debat' && (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center text-2xl">
                      🎭
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Salle de Débat</h2>
                      <p className="text-sm text-slate-400">
                        Lancez un débat où vos directeurs IA discutent et réagissent aux propos des autres
                      </p>
                    </div>
                  </div>
                </div>

                {/* Configuration du débat */}
                {debatResponses.length === 0 && !debatLoading && (
                  <div className="space-y-4">
                    {/* Sujet du débat */}
                    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        🎯 Sujet du débat
                      </label>
                      <textarea
                        value={debatSujet}
                        onChange={(e) => setDebatSujet(e.target.value)}
                        placeholder="Ex: Comment améliorer le taux de confirmation des RDV R1 ?"
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 resize-none"
                        rows={3}
                      />
                    </div>

                    {/* Sélection des participants */}
                    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-medium text-slate-300">
                          👥 Participants au débat ({debatParticipants.length} sélectionnés)
                        </label>
                        <div className="flex gap-2">
                          <button
                            onClick={selectAllDebatParticipants}
                            className="text-xs text-blue-400 hover:text-blue-300"
                          >
                            Tout sélectionner
                          </button>
                          <span className="text-slate-600">|</span>
                          <button
                            onClick={clearDebatParticipants}
                            className="text-xs text-slate-400 hover:text-slate-300"
                          >
                            Effacer
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {directors.map(director => (
                          <button
                            key={director.director_id}
                            onClick={() => toggleDebatParticipant(director.director_id)}
                            className={`flex items-center gap-2 p-2 rounded-lg border transition-all text-left ${
                              debatParticipants.includes(director.director_id)
                                ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                                : 'bg-slate-700/30 border-slate-600/30 text-slate-400 hover:border-slate-500'
                            }`}
                          >
                            <span className="text-lg">{director.avatar}</span>
                            <span className="text-xs truncate">{director.name.split(' ')[0]}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Sélection des collaborateurs humains */}
                    {humanCollaborators.length > 0 && (
                      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-medium text-slate-300">
                            🧑‍💼 Collaborateurs humains ({selectedHumans.length} invités)
                          </label>
                          <button
                            onClick={clearHumanParticipants}
                            className="text-xs text-slate-400 hover:text-slate-300"
                          >
                            Effacer
                          </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {humanCollaborators.map(collab => (
                            <button
                              key={collab.email}
                              onClick={() => toggleHumanParticipant(collab.email)}
                              className={`flex items-center gap-2 p-2 rounded-lg border transition-all text-left ${
                                selectedHumans.includes(collab.email)
                                  ? 'bg-green-500/20 border-green-500/50 text-green-300'
                                  : 'bg-slate-700/30 border-slate-600/30 text-slate-400 hover:border-slate-500'
                              }`}
                            >
                              <span className="text-lg">👤</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs truncate font-medium">{collab.name}</div>
                                <div className="text-[10px] opacity-60 truncate">{collab.email}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                        
                        {selectedHumans.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-700/50">
                            <p className="text-xs text-slate-400 mb-2">Options de notification :</p>
                            <div className="flex gap-4">
                              <label className="flex items-center gap-2 text-xs text-slate-300">
                                <input 
                                  type="checkbox" 
                                  checked={Object.values(humanNotifyOptions).some(o => o.notify_app)}
                                  onChange={(e) => {
                                    const newOptions = {};
                                    selectedHumans.forEach(email => {
                                      newOptions[email] = { 
                                        ...humanNotifyOptions[email], 
                                        notify_app: e.target.checked 
                                      };
                                    });
                                    setHumanNotifyOptions(prev => ({...prev, ...newOptions}));
                                  }}
                                  className="rounded"
                                />
                                🔔 Notification app
                              </label>
                              <label className="flex items-center gap-2 text-xs text-slate-300">
                                <input 
                                  type="checkbox"
                                  checked={Object.values(humanNotifyOptions).some(o => o.notify_email)}
                                  onChange={(e) => {
                                    const newOptions = {};
                                    selectedHumans.forEach(email => {
                                      newOptions[email] = { 
                                        ...humanNotifyOptions[email], 
                                        notify_email: e.target.checked 
                                      };
                                    });
                                    setHumanNotifyOptions(prev => ({...prev, ...newOptions}));
                                  }}
                                  className="rounded"
                                />
                                📧 Notification email
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Option consultant externe */}
                    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                          🧠 Inviter un consultant externe
                        </label>
                        <button
                          onClick={() => setIncludeExternalConsultant(!includeExternalConsultant)}
                          className={`w-12 h-6 rounded-full transition-colors ${
                            includeExternalConsultant ? 'bg-green-500' : 'bg-slate-600'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${
                            includeExternalConsultant ? 'translate-x-6' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                      
                      {includeExternalConsultant && (
                        <div className="flex gap-2 mt-3">
                          {[
                            { id: 'gpt', label: '🤖 GPT-4o', desc: 'OpenAI' },
                            { id: 'claude', label: '🧠 Claude', desc: 'Anthropic' },
                            { id: 'gemini', label: '💎 Gemini', desc: 'Google' }
                          ].map(model => (
                            <button
                              key={model.id}
                              onClick={() => setExternalModel(model.id)}
                              className={`flex-1 p-3 rounded-lg border transition-all ${
                                externalModel === model.id
                                  ? 'bg-blue-500/20 border-blue-500/50 text-blue-300'
                                  : 'bg-slate-700/30 border-slate-600/30 text-slate-400 hover:border-slate-500'
                              }`}
                            >
                              <div className="text-lg mb-1">{model.label}</div>
                              <div className="text-xs opacity-60">{model.desc}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Bouton lancer le débat */}
                    <button
                      onClick={startDebat}
                      disabled={!debatSujet.trim() || debatParticipants.length < 2}
                      className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-slate-600 disabled:to-slate-600 disabled:cursor-not-allowed rounded-xl text-white font-bold text-lg transition-all flex items-center justify-center gap-2"
                    >
                      <Sparkles className="w-5 h-5" />
                      Lancer le débat
                    </button>
                  </div>
                )}

                {/* Chargement du débat */}
                {debatLoading && (
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-8 text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-purple-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">Débat en cours...</h3>
                    <p className="text-slate-400">Les directeurs échangent leurs points de vue</p>
                    <div className="flex justify-center gap-2 mt-4">
                      {debatParticipants.map((id, idx) => {
                        const director = directors.find(d => d.director_id === id);
                        return (
                          <div 
                            key={id}
                            className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-lg animate-bounce"
                            style={{ animationDelay: `${idx * 0.1}s` }}
                          >
                            {director?.avatar || '👤'}
                          </div>
                        );
                      })}
                      {includeExternalConsultant && (
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-lg animate-bounce">
                          {externalModel === 'claude' ? '🧠' : externalModel === 'gemini' ? '💎' : '🤖'}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Résultats du débat */}
                {debatResponses.length > 0 && !debatLoading && (
                  <div className="space-y-4">
                    {/* Header avec sujet et stats */}
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">👑</span>
                          <div>
                            <p className="text-sm text-amber-400 font-medium">Sujet du débat</p>
                            <p className="text-white">{debatSujet}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-center">
                            <p className="text-lg font-bold text-amber-400">{debatTotalRounds}</p>
                            <p className="text-xs text-slate-400">Tour(s)</p>
                          </div>
                          {debatConsensus && (
                            <div className="px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
                              <span className="text-emerald-400 text-sm font-medium">✅ Consensus</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Réponses groupées par tour */}
                    {(() => {
                      const responsesByRound = debatResponses.reduce((acc, r) => {
                        const round = r.round || 1;
                        if (!acc[round]) acc[round] = [];
                        acc[round].push(r);
                        return acc;
                      }, {});
                      
                      return Object.entries(responsesByRound).map(([round, responses]) => (
                        <div key={round} className="space-y-3">
                          {/* Indicateur de tour */}
                          {parseInt(round) > 0 && (
                            <div className="flex items-center gap-3 py-2">
                              <div className="h-px flex-1 bg-slate-700"></div>
                              <span className="px-3 py-1 bg-slate-800 border border-slate-700 rounded-full text-xs text-slate-400">
                                Tour {round}
                              </span>
                              <div className="h-px flex-1 bg-slate-700"></div>
                            </div>
                          )}
                          
                          {responses.map((response, idx) => (
                            <div 
                              key={`${round}-${idx}`}
                              className={`rounded-xl p-4 border ${
                                response.is_synthesis
                                  ? 'bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border-emerald-500/30'
                                  : response.is_external 
                                    ? 'bg-blue-500/10 border-blue-500/30' 
                                    : 'bg-slate-800/50 border-slate-700/50'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${
                                  response.is_synthesis
                                    ? 'bg-emerald-500/20'
                                    : response.is_external 
                                      ? 'bg-blue-500/20' 
                                      : 'bg-slate-700'
                                }`}>
                                  {response.sender_avatar}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className={`font-semibold ${
                                      response.is_synthesis ? 'text-emerald-400' : 'text-white'
                                    }`}>{response.sender_name}</span>
                                    <span className="text-xs text-slate-500">{response.sender_role}</span>
                                    {!response.is_synthesis && (
                                      <span className="text-xs text-slate-600">#{response.order}</span>
                                    )}
                                    {response.is_external && (
                                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                                        Consultant Externe
                                      </span>
                                    )}
                                    {response.is_human && (
                                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                                        Collaborateur
                                      </span>
                                    )}
                                    {response.is_synthesis && response.consensus_reached && (
                                      <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                                        ✅ Consensus atteint
                                      </span>
                                    )}
                                    {response.is_visual && (
                                      <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                                        🎨 Visuel créé
                                      </span>
                                    )}
                                    {response.is_document && (
                                      <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                                        📄 Document créé
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{response.content}</p>
                                  
                                  {/* Boutons Canva si c'est un design Canva */}
                                  {response.is_canva && (response.canva_edit_url || response.canva_view_url) && (
                                    <div className="mt-3 p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                                      <div className="flex flex-wrap gap-3">
                                        {response.canva_edit_url && (
                                          <a 
                                            href={response.canva_edit_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                                          >
                                            ✏️ Modifier sur Canva
                                          </a>
                                        )}
                                        {response.canva_view_url && (
                                          <a 
                                            href={response.canva_view_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                                          >
                                            👁️ Voir le design
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Afficher l'image si c'est un visuel généré par IA (pas Canva) */}
                                  {response.visual_url && !response.is_canva && (
                                    <div className="mt-3 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                                      <a 
                                        href={response.visual_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="block"
                                      >
                                        <img 
                                          src={response.visual_url} 
                                          alt="Visuel créé par David"
                                          className="max-w-full max-h-64 rounded-lg shadow-lg hover:opacity-90 transition-opacity"
                                        />
                                        <p className="text-xs text-purple-400 mt-2 hover:underline">
                                          Cliquez pour voir en taille réelle →
                                        </p>
                                      </a>
                                    </div>
                                  )}
                                  
                                  {/* Afficher les boutons de téléchargement si c'est un document généré */}
                                  {response.is_document && response.document_id && (
                                    <div className="mt-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                                      <div className="flex items-center gap-2 mb-3">
                                        <FileText className="w-5 h-5 text-emerald-400" />
                                        <span className="text-sm font-medium text-emerald-300">
                                          Document professionnel prêt !
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap gap-3">
                                        <a 
                                          href={`${API}/documents/${response.document_id}/preview`}
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                                          data-testid={`preview-doc-${response.document_id}`}
                                        >
                                          👁️ Prévisualiser
                                        </a>
                                        <a 
                                          href={`${API}/documents/${response.document_id}/download`}
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                                          data-testid={`download-doc-${response.document_id}`}
                                        >
                                          📥 Télécharger HTML
                                        </a>
                                        {response.document_data_url && (
                                          <a 
                                            href={response.document_data_url}
                                            download={response.document_filename || 'document.html'}
                                            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                                            data-testid={`instant-download-${response.document_id}`}
                                          >
                                            ⚡ Téléchargement direct
                                          </a>
                                        )}
                                      </div>
                                      <p className="text-xs text-slate-400 mt-2">
                                        💡 Astuce : Ouvrez le fichier HTML dans votre navigateur et utilisez "Imprimer → PDF" pour créer un PDF
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ));
                    })()}

                    {/* Zone d'intervention humaine (Mode Mixte) */}
                    {activeDebatId && currentUserEmail && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg">✋</span>
                          <span className="font-medium text-green-300">Votre intervention</span>
                          <span className="text-xs text-slate-400">({currentUserEmail})</span>
                        </div>
                        <textarea
                          value={humanIntervention}
                          onChange={(e) => setHumanIntervention(e.target.value)}
                          placeholder="Partagez votre point de vue sur ce débat..."
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 resize-none"
                          rows={3}
                        />
                        <div className="flex justify-end mt-2">
                          <button
                            onClick={submitHumanIntervention}
                            disabled={!humanIntervention.trim()}
                            className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors flex items-center gap-2"
                          >
                            <Send className="w-4 h-4" />
                            Intervenir
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Actions après débat */}
                    <div className="flex flex-col gap-3 pt-4">
                      {/* Zone d'intervention PDG */}
                      {activeDebatId && !debatSynthesis && (
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">👑</span>
                              <span className="font-medium text-amber-300">Intervention PDG</span>
                            </div>
                            {!showPdgPrompt && (
                              <button
                                onClick={() => setShowPdgPrompt(true)}
                                className="px-3 py-1 bg-amber-600 hover:bg-amber-500 rounded-lg text-sm text-white flex items-center gap-2"
                              >
                                <MessageSquare className="w-4 h-4" />
                                Guider le débat
                              </button>
                            )}
                          </div>
                          
                          {showPdgPrompt && (
                            <div className="space-y-3">
                              <p className="text-xs text-slate-400">
                                Donnez une directive aux participants. Utilisez <span className="text-amber-400 font-medium">@</span> pour mentionner un participant spécifique.
                              </p>
                              <div className="relative">
                                <textarea
                                  ref={pdgPromptRef}
                                  value={pdgPrompt}
                                  onChange={handlePdgPromptChange}
                                  placeholder="Ex: @Dr. Gemini que pensez-vous de la proposition de Yaël ?"
                                  className="w-full bg-slate-900 border border-amber-500/30 rounded-lg px-4 py-3 text-white placeholder-slate-500 resize-none"
                                  rows={3}
                                />
                                
                                {/* Liste déroulante des mentions */}
                                {showMentionList && (
                                  <div className="absolute bottom-full left-0 w-full mb-1 bg-slate-800 border border-amber-500/30 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                                    <div className="p-2 text-xs text-slate-400 border-b border-slate-700">
                                      Sélectionnez un participant
                                    </div>
                                    {getMentionableParticipants()
                                      .filter(p => p.searchName.includes(mentionSearch))
                                      .map(participant => (
                                        <button
                                          key={participant.id}
                                          onClick={() => insertMention(participant)}
                                          className="w-full px-3 py-2 flex items-center gap-3 hover:bg-amber-500/20 transition-colors text-left"
                                        >
                                          <span className="text-xl">{participant.avatar}</span>
                                          <div>
                                            <div className="text-white font-medium">{participant.name}</div>
                                            <div className="text-xs text-slate-400">
                                              {participant.type === 'external' ? 'Consultant Externe' : 'Agent IA'}
                                            </div>
                                          </div>
                                        </button>
                                      ))}
                                    {getMentionableParticipants().filter(p => p.searchName.includes(mentionSearch)).length === 0 && (
                                      <div className="px-3 py-2 text-slate-500 text-sm">Aucun participant trouvé</div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={() => { setShowPdgPrompt(false); setPdgPrompt(''); }}
                                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                                >
                                  Annuler
                                </button>
                                <button
                                  onClick={submitPdgIntervention}
                                  disabled={!pdgPrompt.trim() || pdgPromptSending}
                                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 rounded-lg text-sm flex items-center gap-2"
                                >
                                  {pdgPromptSending ? (
                                    <>
                                      <RefreshCw className="w-4 h-4 animate-spin" />
                                      Envoi...
                                    </>
                                  ) : (
                                    <>
                                      <Send className="w-4 h-4" />
                                      Envoyer et continuer
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Zone de feedback PDG après synthèse */}
                      {debatSynthesis && activeDebatId && (
                        <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xl">🤔</span>
                            <span className="font-medium text-red-300">Pas satisfait de la conclusion ?</span>
                          </div>
                          
                          {!showPdgPrompt ? (
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => {
                                  setShowPdgPrompt(true);
                                  setPdgPrompt("Vous êtes hors-sujet. Revenez au sujet principal : ");
                                }}
                                className="px-3 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg text-sm text-red-300 flex items-center gap-2"
                              >
                                🎯 Hors-sujet - Recadrer
                              </button>
                              <button
                                onClick={() => {
                                  setShowPdgPrompt(true);
                                  setPdgPrompt("Je veux des propositions plus concrètes et actionnables. Précisez : ");
                                }}
                                className="px-3 py-2 bg-orange-600/20 hover:bg-orange-600/30 border border-orange-500/30 rounded-lg text-sm text-orange-300 flex items-center gap-2"
                              >
                                📋 Plus de détails
                              </button>
                              <button
                                onClick={() => {
                                  setShowPdgPrompt(true);
                                  setPdgPrompt("Proposez un plan d'action précis avec des responsables et des délais : ");
                                }}
                                className="px-3 py-2 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/30 rounded-lg text-sm text-amber-300 flex items-center gap-2"
                              >
                                📅 Plan d'action
                              </button>
                              <button
                                onClick={() => setShowPdgPrompt(true)}
                                className="px-3 py-2 bg-slate-600/20 hover:bg-slate-600/30 border border-slate-500/30 rounded-lg text-sm text-slate-300 flex items-center gap-2"
                              >
                                ✏️ Autre directive
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <textarea
                                value={pdgPrompt}
                                onChange={(e) => setPdgPrompt(e.target.value)}
                                placeholder="Expliquez ce que vous attendez..."
                                className="w-full bg-slate-900 border border-red-500/30 rounded-lg px-4 py-3 text-white placeholder-slate-500 resize-none"
                                rows={3}
                              />
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={() => { setShowPdgPrompt(false); setPdgPrompt(''); }}
                                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                                >
                                  Annuler
                                </button>
                                <button
                                  onClick={() => {
                                    setDebatSynthesis(null); // Supprimer la synthèse pour continuer
                                    submitPdgIntervention();
                                  }}
                                  disabled={!pdgPrompt.trim() || pdgPromptSending}
                                  className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-slate-600 rounded-lg text-sm flex items-center gap-2"
                                >
                                  {pdgPromptSending ? (
                                    <>
                                      <RefreshCw className="w-4 h-4 animate-spin" />
                                      Relance...
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="w-4 h-4" />
                                      Relancer le débat
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Boutons d'action */}
                      <div className="flex gap-3">
                        {/* Bouton continuer si pas de synthèse */}
                        {!debatSynthesis && activeDebatId && (
                          <button
                            onClick={continueDebat}
                            disabled={continuingDebat || pdgPromptSending}
                            className="flex-1 py-3 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 rounded-xl text-white font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            {continuingDebat ? (
                              <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                Tour en cours...
                              </>
                            ) : (
                              <>
                                <MessageSquare className="w-4 h-4" />
                                Continuer le débat (Tour {debatTotalRounds + 1})
                              </>
                            )}
                          </button>
                        )}
                        <button
                          onClick={resetDebat}
                          className={`${debatSynthesis && !showPdgPrompt ? 'flex-1' : ''} py-3 px-6 bg-slate-700 hover:bg-slate-600 rounded-xl text-white font-medium transition-colors flex items-center justify-center gap-2`}
                        >
                          <RefreshCw className="w-4 h-4" />
                          Nouveau débat
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ONGLET CRÉATIONS - Demandes à David */}
          {activeTab === 'creations' && (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Palette className="w-5 h-5 text-purple-400" />
                  Demandes de Création à David
                </h3>
                <button
                  onClick={() => setShowNewCreationRequest(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Nouvelle demande
                </button>
              </div>

              {/* Info David + Outils disponibles */}
              <div className="mb-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/20">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">📣</span>
                  <div className="flex-1">
                    <h4 className="font-medium text-purple-300">David Ben-Ari - Marketing & Communication</h4>
                    <p className="text-sm text-slate-400 mt-1">
                      David peut créer des présentations, flyers, posts réseaux sociaux, documents de formation et infographies pour tous les pôles.
                    </p>
                    {davidTools.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="text-xs text-slate-500 mr-1 self-center">Outils IA :</span>
                        {davidTools.map(t => (
                          <span 
                            key={t.id}
                            data-testid={`david-tool-${t.id}`}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                              t.enabled 
                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' 
                                : 'bg-slate-700/50 text-slate-500 border border-slate-600'
                            }`}
                          >
                            {t.type === 'image' ? <Image className="w-3 h-3" /> : t.type === 'video' ? <Video className="w-3 h-3" /> : <Palette className="w-3 h-3" />}
                            {t.name}
                            {!t.enabled && <span className="text-[10px] opacity-60">({t.reason})</span>}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ═══════════════════════════════════════════════════════════════ */}
              {/* GÉNÉRATEUR DE DOCUMENTS PERSONNALISÉS */}
              {/* ═══════════════════════════════════════════════════════════════ */}
              <div className="mb-8 p-5 rounded-xl bg-gradient-to-br from-emerald-900/30 to-slate-800/30 border border-emerald-500/30">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-emerald-300 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Générateur de Documents Professionnels
                  </h4>
                  {generatedDocResult && (
                    <button
                      onClick={resetDocGeneratorForm}
                      className="text-xs text-slate-400 hover:text-white"
                    >
                      ✕ Nouveau document
                    </button>
                  )}
                </div>

                {!generatedDocResult ? (
                  <>
                    {/* Sélecteurs Template et Thème */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Type de document</label>
                        <select
                          value={docGeneratorForm.template_type}
                          onChange={(e) => setDocGeneratorForm(prev => ({...prev, template_type: e.target.value}))}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                          data-testid="doc-template-select"
                        >
                          <option value="formation">📚 Guide de Formation</option>
                          <option value="commercial">💼 Proposition Commerciale</option>
                          <option value="rapport">📊 Rapport d'Activité</option>
                          <option value="presentation">🎬 Présentation</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Style visuel</label>
                        <select
                          value={docGeneratorForm.theme}
                          onChange={(e) => setDocGeneratorForm(prev => ({...prev, theme: e.target.value}))}
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                          data-testid="doc-theme-select"
                        >
                          {availableThemes.length > 0 ? (
                            availableThemes.map(theme => (
                              <option key={theme.id} value={theme.id}>
                                {theme.name} - {theme.description}
                              </option>
                            ))
                          ) : (
                            <>
                              <option value="default">SANI ADAPT - Bleu et vert</option>
                              <option value="elegant">Élégant - Bleu marine et or</option>
                              <option value="modern">Moderne - Violet et cyan</option>
                              <option value="nature">Nature - Verts naturels</option>
                              <option value="corporate">Corporate - Bleus professionnels</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>

                    {/* Titre et Sous-titre */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Titre du document *</label>
                        <input
                          type="text"
                          value={docGeneratorForm.title}
                          onChange={(e) => setDocGeneratorForm(prev => ({...prev, title: e.target.value}))}
                          placeholder="Ex: Formation Prospection Téléphonique"
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                          data-testid="doc-title-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Sous-titre</label>
                        <input
                          type="text"
                          value={docGeneratorForm.subtitle}
                          onChange={(e) => setDocGeneratorForm(prev => ({...prev, subtitle: e.target.value}))}
                          placeholder="Ex: Les techniques avancées"
                          className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                          data-testid="doc-subtitle-input"
                        />
                      </div>
                    </div>

                    {/* Champs spécifiques par type de document */}
                    {docGeneratorForm.template_type === 'formation' && (
                      <div className="mb-4">
                        <label className="block text-xs text-slate-400 mb-2">
                          Points clés ({(docGeneratorForm.key_points || []).length}/5)
                          <button
                            onClick={addKeyPoint}
                            disabled={(docGeneratorForm.key_points || []).length >= 5}
                            className="ml-2 text-emerald-400 hover:text-emerald-300 disabled:opacity-50"
                          >
                            + Ajouter
                          </button>
                        </label>
                        <div className="space-y-2">
                          {(docGeneratorForm.key_points || []).map((kp, idx) => (
                            <div key={idx} className="flex gap-2">
                              <input
                                type="text"
                                value={kp.title}
                                onChange={(e) => updateKeyPoint(idx, 'title', e.target.value)}
                                placeholder="Titre du point"
                                className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                              />
                              <input
                                type="text"
                                value={kp.description}
                                onChange={(e) => updateKeyPoint(idx, 'description', e.target.value)}
                                placeholder="Description"
                                className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                              />
                              <button
                                onClick={() => removeKeyPoint(idx)}
                                className="px-2 text-red-400 hover:text-red-300"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {docGeneratorForm.template_type === 'commercial' && (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Nom du client</label>
                            <input
                              type="text"
                              value={docGeneratorForm.client_name}
                              onChange={(e) => setDocGeneratorForm(prev => ({...prev, client_name: e.target.value}))}
                              placeholder="Ex: M. et Mme Dupont"
                              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Prix total TTC</label>
                            <input
                              type="text"
                              value={docGeneratorForm.total_price}
                              onChange={(e) => setDocGeneratorForm(prev => ({...prev, total_price: e.target.value}))}
                              placeholder="Ex: 12 500 €"
                              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                        <div className="mb-4">
                          <label className="block text-xs text-slate-400 mb-2">
                            Services / Prestations
                            <button
                              onClick={addService}
                              className="ml-2 text-emerald-400 hover:text-emerald-300"
                            >
                              + Ajouter
                            </button>
                          </label>
                          <div className="space-y-2">
                            {(docGeneratorForm.services || []).map((svc, idx) => (
                              <div key={idx} className="flex gap-2">
                                <input
                                  type="text"
                                  value={svc.name}
                                  onChange={(e) => updateService(idx, 'name', e.target.value)}
                                  placeholder="Nom"
                                  className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                                />
                                <input
                                  type="text"
                                  value={svc.description}
                                  onChange={(e) => updateService(idx, 'description', e.target.value)}
                                  placeholder="Description"
                                  className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                                />
                                <input
                                  type="text"
                                  value={svc.price}
                                  onChange={(e) => updateService(idx, 'price', e.target.value)}
                                  placeholder="Prix"
                                  className="w-24 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                                />
                                <button
                                  onClick={() => removeService(idx)}
                                  className="px-2 text-red-400 hover:text-red-300"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    {docGeneratorForm.template_type === 'rapport' && (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Période</label>
                            <input
                              type="text"
                              value={docGeneratorForm.period}
                              onChange={(e) => setDocGeneratorForm(prev => ({...prev, period: e.target.value}))}
                              placeholder="Ex: Décembre 2025"
                              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Département</label>
                            <input
                              type="text"
                              value={docGeneratorForm.department}
                              onChange={(e) => setDocGeneratorForm(prev => ({...prev, department: e.target.value}))}
                              placeholder="Ex: Télépro"
                              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                        </div>
                        <div className="mb-4">
                          <label className="block text-xs text-slate-400 mb-2">
                            Statistiques clés
                            <button
                              onClick={addStat}
                              className="ml-2 text-emerald-400 hover:text-emerald-300"
                            >
                              + Ajouter
                            </button>
                          </label>
                          <div className="space-y-2">
                            {(docGeneratorForm.stats || []).map((stat, idx) => (
                              <div key={idx} className="flex gap-2">
                                <input
                                  type="text"
                                  value={stat.value}
                                  onChange={(e) => updateStat(idx, 'value', e.target.value)}
                                  placeholder="Valeur (ex: 98%)"
                                  className="w-24 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                                />
                                <input
                                  type="text"
                                  value={stat.label}
                                  onChange={(e) => updateStat(idx, 'label', e.target.value)}
                                  placeholder="Libellé (ex: Taux de conversion)"
                                  className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                                />
                                <select
                                  value={stat.trend}
                                  onChange={(e) => updateStat(idx, 'trend', e.target.value)}
                                  className="w-28 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                                >
                                  <option value="">Tendance</option>
                                  <option value="up">📈 Hausse</option>
                                  <option value="down">📉 Baisse</option>
                                  <option value="stable">➡️ Stable</option>
                                </select>
                                <button
                                  onClick={() => removeStat(idx)}
                                  className="px-2 text-red-400 hover:text-red-300"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Bouton de génération */}
                    <button
                      onClick={generateCustomDocument}
                      disabled={generatingDoc || !docGeneratorForm.title.trim()}
                      className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                      data-testid="generate-doc-btn"
                    >
                      {generatingDoc ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Génération en cours...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4" />
                          Générer le document
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  /* Résultat de la génération */
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-emerald-500/20 border border-emerald-500/40">
                      <div className="flex items-center gap-2 mb-2">
                        <Check className="w-5 h-5 text-emerald-400" />
                        <span className="font-medium text-emerald-300">Document généré avec succès !</span>
                      </div>
                      <p className="text-sm text-slate-400">{generatedDocResult.filename}</p>
                    </div>
                    
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={`${API}/documents/${generatedDocResult.doc_id}/preview`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                        data-testid="preview-generated-doc"
                      >
                        👁️ Prévisualiser
                      </a>
                      <a
                        href={`${API}/documents/${generatedDocResult.doc_id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                        data-testid="download-generated-doc"
                      >
                        📥 Télécharger HTML
                      </a>
                      {generatedDocResult.data_url && (
                        <a
                          href={generatedDocResult.data_url}
                          download={generatedDocResult.filename}
                          className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                        >
                          ⚡ Téléchargement direct
                        </a>
                      )}
                    </div>
                    
                    <p className="text-xs text-slate-400">
                      💡 Astuce : Ouvrez le fichier HTML dans votre navigateur et utilisez "Imprimer → PDF" pour créer un PDF
                    </p>
                  </div>
                )}
              </div>

              {/* Formulaire nouvelle demande */}
              {showNewCreationRequest && (
                <div className="mb-6 p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <Plus className="w-4 h-4 text-purple-400" />
                    Nouvelle demande de création
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {/* Agent demandeur */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Qui demande ? *</label>
                      <select
                        value={creationForm.requester_id}
                        onChange={(e) => setCreationForm(prev => ({...prev, requester_id: e.target.value}))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">Sélectionnez un agent</option>
                        {directors.map(d => (
                          <option key={d.director_id} value={d.director_id}>
                            {d.avatar} {d.name} - {d.pole}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Type de création */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Type de création *</label>
                      <select
                        value={creationForm.creation_type}
                        onChange={(e) => setCreationForm(prev => ({...prev, creation_type: e.target.value}))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="presentation">📊 Présentation</option>
                        <option value="flyer">📄 Flyer / Affiche</option>
                        <option value="social_post">📱 Post Réseaux Sociaux</option>
                        <option value="training_doc">📚 Document de Formation</option>
                        <option value="infographic">📈 Infographie</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Titre */}
                  <div className="mb-4">
                    <label className="block text-xs text-slate-400 mb-1">Titre *</label>
                    <input
                      type="text"
                      value={creationForm.title}
                      onChange={(e) => setCreationForm(prev => ({...prev, title: e.target.value}))}
                      placeholder="Ex: Guide d'accueil des nouveaux télépros"
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  
                  {/* Description */}
                  <div className="mb-4">
                    <label className="block text-xs text-slate-400 mb-1">Description *</label>
                    <textarea
                      value={creationForm.description}
                      onChange={(e) => setCreationForm(prev => ({...prev, description: e.target.value}))}
                      placeholder="Décrivez ce que vous voulez, le contenu, le style souhaité..."
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm resize-none"
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    {/* Audience cible */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Audience cible</label>
                      <input
                        type="text"
                        value={creationForm.target_audience}
                        onChange={(e) => setCreationForm(prev => ({...prev, target_audience: e.target.value}))}
                        placeholder="Ex: Nouveaux télépros, Techniciens..."
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    {/* Urgence */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Urgence</label>
                      <select
                        value={creationForm.urgency}
                        onChange={(e) => setCreationForm(prev => ({...prev, urgency: e.target.value}))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="low">🟢 Basse</option>
                        <option value="normal">🟡 Normale</option>
                        <option value="high">🔴 Haute</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Instructions additionnelles */}
                  <div className="mb-4">
                    <label className="block text-xs text-slate-400 mb-1">Instructions additionnelles</label>
                    <textarea
                      value={creationForm.additional_instructions}
                      onChange={(e) => setCreationForm(prev => ({...prev, additional_instructions: e.target.value}))}
                      placeholder="Couleurs spécifiques, logo à inclure, format..."
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm resize-none"
                      rows={2}
                    />
                  </div>
                  
                  {/* Boutons */}
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setShowNewCreationRequest(false)}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={submitCreationRequest}
                      disabled={submittingCreation}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                      {submittingCreation ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Envoi à David...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Envoyer à David
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Liste des demandes */}
              <div className="space-y-4">
                {creationRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <Palette className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">Aucune demande de création pour le moment</p>
                    <p className="text-sm text-slate-500 mt-2">
                      Cliquez sur "Nouvelle demande" pour créer un visuel
                    </p>
                  </div>
                ) : (
                  creationRequests.map(req => (
                    <div key={req.request_id} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{req.requester_avatar}</span>
                          <div>
                            <h4 className="font-medium">{req.title}</h4>
                            <p className="text-xs text-slate-400">
                              Demandé par {req.requester_name} • {new Date(req.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          req.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          req.status === 'in_progress' ? 'bg-amber-500/20 text-amber-400' :
                          req.status === 'generating' ? 'bg-blue-500/20 text-blue-400' :
                          req.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {req.status === 'completed' ? 'Termine' : 
                           req.status === 'in_progress' ? 'En cours' :
                           req.status === 'generating' ? 'Generation IA...' :
                           req.status === 'failed' ? 'Echec' : 'En attente'}
                        </span>
                      </div>
                      
                      <p className="text-sm text-slate-300 mb-3">{req.description}</p>
                      
                      {req.david_response && (
                        <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20 mb-3">
                          <p className="text-xs text-purple-400 font-medium mb-1">Reponse de David :</p>
                          <p className="text-sm text-slate-300">{req.david_response}</p>
                        </div>
                      )}
                      
                      {/* Resultat image */}
                      {req.result_url && req.result_type === 'image' && (
                        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 mb-3">
                          <p className="text-xs text-green-400 font-medium mb-2 flex items-center gap-1">
                            <Image className="w-3.5 h-3.5" /> Visuel genere via {req.result_tool === 'gpt_image' ? 'GPT Image 1' : req.result_tool || 'IA'}
                          </p>
                          <img 
                            src={req.result_url} 
                            alt={req.title}
                            className="rounded-lg max-w-full max-h-80 border border-slate-600"
                            data-testid={`result-image-${req.request_id}`}
                          />
                        </div>
                      )}

                      {/* Resultat video */}
                      {req.result_url && req.result_type === 'video' && (
                        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 mb-3">
                          <p className="text-xs text-green-400 font-medium mb-2 flex items-center gap-1">
                            <Video className="w-3.5 h-3.5" /> Video generee via Sora 2
                          </p>
                          <video 
                            src={req.result_url}
                            controls 
                            className="rounded-lg max-w-full max-h-80 border border-slate-600"
                            data-testid={`result-video-${req.request_id}`}
                          />
                        </div>
                      )}

                      {/* Legacy: ancien result_url sans type */}
                      {req.result_url && !req.result_type && (
                        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 mb-3">
                          <a href={req.result_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 hover:underline">
                            Voir le visuel
                          </a>
                        </div>
                      )}
                      
                      {/* Generation en cours (Sora 2 background) */}
                      {req.status === 'generating' && (
                        <div className="mt-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center gap-3">
                          <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />
                          <div>
                            <p className="text-sm text-blue-300 font-medium">Generation Sora 2 en cours...</p>
                            <p className="text-xs text-slate-400">La video sera prete dans 3-5 minutes. Actualisez la page.</p>
                          </div>
                        </div>
                      )}

                      {/* Echec */}
                      {req.status === 'failed' && (
                        <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-400" />
                          <div>
                            <p className="text-sm text-red-300">Echec de la generation</p>
                            <p className="text-xs text-slate-400">{req.error_message || 'Erreur inconnue. Reessayez.'}</p>
                          </div>
                        </div>
                      )}

                      {/* Bouton generation avec choix d'outil */}
                      {(req.status === 'in_progress' || req.status === 'failed') && !req.result_url && (
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          {davidTools.filter(t => t.enabled).map(t => (
                            <button
                              key={t.id}
                              disabled={generatingVisual === req.request_id}
                              onClick={() => generateVisual(req.request_id, t.id)}
                              data-testid={`generate-${t.id}-${req.request_id}`}
                              className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all disabled:opacity-50 ${
                                t.type === 'image' 
                                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500' 
                                  : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500'
                              }`}
                            >
                              {generatingVisual === req.request_id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : t.type === 'image' ? (
                                <Image className="w-4 h-4" />
                              ) : (
                                <Video className="w-4 h-4" />
                              )}
                              {generatingVisual === req.request_id ? 'Generation en cours...' : `Generer avec ${t.name}`}
                            </button>
                          ))}
                          {davidTools.filter(t => t.enabled).length === 0 && (
                            <p className="text-xs text-amber-400">Aucun outil actif. Activez les permissions apps de David dans Permissions IA.</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="flex-1 overflow-y-auto p-4">
              {/* Historique des Débats */}
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Historique des Débats
              </h3>
              {pastDebats.length === 0 ? (
                <div className="text-center py-8 mb-8">
                  <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">Aucun débat pour le moment</p>
                </div>
              ) : (
                <div className="space-y-3 mb-8">
                  {pastDebats.map(debat => (
                    <div 
                      key={debat.debat_id}
                      onClick={() => reopenDebat(debat)}
                      className="bg-slate-800/50 border border-purple-500/20 rounded-xl p-4 hover:border-purple-500/50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-white truncate">{debat.sujet}</h4>
                        <span className="px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400 flex-shrink-0 ml-2">
                          {debat.total_rounds || 1} tour{(debat.total_rounds || 1) > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {debat.created_at ? new Date(debat.created_at).toLocaleDateString('fr-FR') : 'Date inconnue'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {debat.participants?.length || 0} participants
                        </span>
                        {debat.include_external && (
                          <span className="flex items-center gap-1 text-amber-400">
                            🤖 + Consultant
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Historique des Réunions */}
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Historique des Réunions
              </h3>
              {reunions.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Aucune réunion planifiée pour le moment</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reunions.map(reunion => (
                    <div 
                      key={reunion.id}
                      onClick={() => openReunion(reunion)}
                      className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 hover:border-amber-500/30 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-white">{reunion.title}</h4>
                        <span className={`px-2 py-1 rounded text-xs ${
                          reunion.status === 'in_progress' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-slate-600/50 text-slate-400'
                        }`}>
                          {reunion.status === 'in_progress' ? 'En cours' : 'Terminée'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(reunion.started_at).toLocaleDateString('fr-FR')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {reunion.participants?.length || 'Tous'}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {reunion.decisions_count || 0} décisions
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Modal Nouvelle Réunion */}
      {showNewReunion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Video className="w-6 h-6 text-amber-400" />
                Nouvelle Réunion
              </h3>
              <button 
                onClick={() => setShowNewReunion(false)}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Titre de la réunion</label>
                <input
                  type="text"
                  value={reunionForm.title}
                  onChange={(e) => setReunionForm({ ...reunionForm, title: e.target.value })}
                  placeholder="Ex: Point hebdomadaire semaine 6"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Type de réunion</label>
                <div className="flex gap-2">
                  {[
                    { id: 'hebdo', label: 'Hebdomadaire' },
                    { id: 'mensuelle', label: 'Mensuelle' },
                    { id: 'exceptionnelle', label: 'Exceptionnelle' }
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => setReunionForm({ ...reunionForm, meeting_type: type.id })}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                        reunionForm.meeting_type === type.id
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                          : 'bg-slate-700/50 text-slate-400 border border-slate-600/30'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Ordre du jour</label>
                <div className="space-y-2">
                  {reunionForm.agenda.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => {
                          const newAgenda = [...reunionForm.agenda];
                          newAgenda[index] = e.target.value;
                          setReunionForm({ ...reunionForm, agenda: newAgenda });
                        }}
                        placeholder={`Point ${index + 1}`}
                        className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm"
                      />
                      {reunionForm.agenda.length > 1 && (
                        <button
                          onClick={() => {
                            const newAgenda = reunionForm.agenda.filter((_, i) => i !== index);
                            setReunionForm({ ...reunionForm, agenda: newAgenda });
                          }}
                          className="p-2 text-slate-400 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setReunionForm({ ...reunionForm, agenda: [...reunionForm.agenda, ''] })}
                    className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter un point
                  </button>
                </div>
              </div>

              {selectedParticipants.length > 0 && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-300">
                    Participants: {selectedParticipants.map(id => getDirectorById(id)?.name).filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowNewReunion(false)}
                className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-white"
              >
                Annuler
              </button>
              <button
                onClick={startReunion}
                disabled={!reunionForm.title.trim()}
                className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-600 rounded-lg text-white font-medium"
              >
                Démarrer la réunion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Base de Connaissances */}
      {showKnowledgeModal && selectedAgent && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-2xl">
                  {selectedAgent.avatar}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-400" />
                    Base de Connaissances
                  </h3>
                  <p className="text-sm text-slate-400">{selectedAgent.name} - {selectedAgent.pole}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowKnowledgeModal(false)}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {loadingKnowledge ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : agentKnowledge.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400 mb-2">Aucune connaissance enregistrée</p>
                  <p className="text-sm text-slate-500">
                    Les connaissances sont extraites automatiquement des conversations
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">{agentKnowledge.length} connaissance(s)</span>
                    <button
                      onClick={clearAllKnowledge}
                      className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Tout effacer
                    </button>
                  </div>
                  
                  {agentKnowledge.map((knowledge, index) => {
                    const typeConfig = {
                      fait: { color: 'blue', emoji: '📌', label: 'Fait' },
                      decision: { color: 'green', emoji: '✅', label: 'Décision' },
                      preference: { color: 'amber', emoji: '⭐', label: 'Préférence' },
                      action: { color: 'purple', emoji: '🎯', label: 'Action' },
                      information: { color: 'slate', emoji: 'ℹ️', label: 'Info' }
                    };
                    const config = typeConfig[knowledge.type] || typeConfig.information;
                    
                    return (
                      <div 
                        key={index}
                        className={`p-4 rounded-xl border bg-${config.color}-500/10 border-${config.color}-500/30 group relative`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg">{config.emoji}</span>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded bg-${config.color}-500/20 text-${config.color}-400`}>
                                {config.label}
                              </span>
                              <span className="text-xs text-slate-500">{knowledge.date}</span>
                              {knowledge.source && (
                                <span className="text-xs text-slate-600">({knowledge.source})</span>
                              )}
                            </div>
                            <p className="text-white">{knowledge.contenu}</p>
                          </div>
                          <button
                            onClick={() => deleteKnowledge(index)}
                            className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-500/20 rounded text-red-400 transition-opacity"
                            title="Supprimer"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Footer - Ajouter une connaissance */}
            <div className="p-6 border-t border-slate-700">
              <p className="text-sm text-slate-400 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Ajouter une connaissance manuellement
              </p>
              <div className="flex gap-3">
                <select
                  value={newKnowledge.type}
                  onChange={(e) => setNewKnowledge({ ...newKnowledge, type: e.target.value })}
                  className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="information">ℹ️ Information</option>
                  <option value="fait">📌 Fait</option>
                  <option value="decision">✅ Décision</option>
                  <option value="preference">⭐ Préférence</option>
                  <option value="action">🎯 Action</option>
                </select>
                <input
                  type="text"
                  value={newKnowledge.contenu}
                  onChange={(e) => setNewKnowledge({ ...newKnowledge, contenu: e.target.value })}
                  placeholder="Entrez la connaissance à ajouter..."
                  className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && addKnowledge()}
                />
                <button
                  onClick={addKnowledge}
                  disabled={!newKnowledge.contenu.trim()}
                  className="px-4 py-2 bg-purple-500 hover:bg-purple-400 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg text-white font-medium"
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Vue Réunion */}
      {selectedReunion && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <Video className="w-6 h-6 text-amber-400" />
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedReunion.title}</h3>
                  <p className="text-sm text-slate-400">
                    {selectedReunion.started_at ? new Date(selectedReunion.started_at).toLocaleDateString('fr-FR', { 
                      weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' 
                    }) : 'Date inconnue'}
                  </p>
                </div>
              </div>
              <button 
                onClick={closeReunion}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Contenu */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingReunion ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Ordre du jour si présent */}
                  {selectedReunion.agenda && selectedReunion.agenda.length > 0 && (
                    <div className="p-4 rounded-xl bg-slate-700/30 border border-slate-600/50">
                      <h4 className="font-medium text-amber-400 mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Ordre du jour
                      </h4>
                      <ul className="space-y-2">
                        {selectedReunion.agenda.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-300">
                            <span className="text-amber-400">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Messages / Interventions */}
                  <div>
                    <h4 className="font-medium text-white mb-4 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-amber-400" />
                      Interventions ({reunionMessages.length})
                    </h4>
                    
                    {reunionMessages.length === 0 ? (
                      <div className="text-center py-8">
                        <MessageSquare className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">Aucune intervention enregistrée</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {reunionMessages.map((msg, idx) => (
                          <div key={msg.id || idx} className="flex gap-3">
                            <span className="text-2xl">{msg.sender_avatar}</span>
                            <div className="flex-1 bg-slate-700/30 rounded-xl p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-white">{msg.sender_name}</span>
                                {msg.sender_role && (
                                  <span className="text-xs text-slate-400">• {msg.sender_role}</span>
                                )}
                              </div>
                              <p className="text-slate-300 text-sm whitespace-pre-wrap">{msg.content}</p>
                              {msg.timestamp && (
                                <p className="text-xs text-slate-500 mt-2">
                                  {new Date(msg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Décisions si présentes */}
                  {selectedReunion.decisions && selectedReunion.decisions.length > 0 && (
                    <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                      <h4 className="font-medium text-green-400 mb-3 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        Décisions prises
                      </h4>
                      <ul className="space-y-2">
                        {selectedReunion.decisions.map((decision, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-300">
                            <span className="text-green-400">✓</span>
                            {decision}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-700 flex justify-end gap-2">
              <button
                onClick={closeReunion}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ComiteDirectionDashboard;
